import React from 'react'
import { getTranslation } from '../translations'

function SectionBar({ title, subtitle }) {
  return (
    <div className="mb-2">
      <div className="bg-[#134225] text-white font-extrabold px-3 py-2 uppercase tracking-wide text-sm text-center">
        {title}
      </div>
      {subtitle ? (
        <div className="text-[12px] italic text-[#334c3e] px-2 py-1 text-center">{subtitle}</div>
      ) : null}
    </div>
  )
}

const dotColor = (status) => {
  if (status === 'low') return 'bg-red-500';
  if (status === 'high') return 'bg-yellow-500';
  if (status === 'ok') return 'bg-green-500';
  return 'bg-gray-400';
}

function StatusDot({ status }) {
  return <span className={`inline-block h-3 w-3 rounded-full ${dotColor(status)}`} />
}

function HeaderRow({ t }) {
  return (
    <tr className="bg-[#134225] text-white text-sm">
      <th className="px-3 py-2 border border-[#d9e4dd] w-20 whitespace-nowrap text-center">{t.sNo}</th>
      <th className="px-3 py-2 border border-[#d9e4dd] text-left">{t.parameter}</th>
      <th className="px-3 py-2 border border-[#d9e4dd] text-left">{t.idealRange}</th>
      <th className="px-3 py-2 border border-[#d9e4dd] text-left">{t.actualValue}</th>
      <th className="px-3 py-2 border border-[#d9e4dd] text-left">{t.status}</th>
    </tr>
  )
}

function calcStatus(actualNumber, actualText, min, max) {
  if (actualText === "BODL" || actualText === "BODL ppm" ) return 'low'

  if (actualNumber == null || Number.isNaN(actualNumber)) return 'na'
  if (min == null || max == null) return 'na'

  if (actualNumber < min) return 'low'
  if (actualNumber > max) return 'high'

  return 'ok'
}

function Row({ index, row }) {
  const status = calcStatus(row.actualNumber, row.actualText, row.min, row.max)
  const parameter = row.parameter ?? '-'
  const idealText = row.idealText ?? '-'
  let actualText
  if (row.actualText === '' || row.actualText === null || row.actualText === undefined) {
    actualText = '-'
  } else {
    actualText = row.actualText
  }
  return (
    <tr className="odd:bg-white even:bg-gray-50 text-sm">
      <td className="px-3 py-2 border border-[#e4ebe7] text-center whitespace-nowrap">{index}</td>
      <td className="px-3 py-2 border border-[#e4ebe7]">{parameter}</td>
      <td className="px-3 py-2 border border-[#e4ebe7]">{idealText}</td>
      <td className="px-3 py-2 border border-[#e4ebe7]">{actualText}</td>
      <td className="px-3 py-2 border border-[#e4ebe7]"><StatusDot status={status} /></td>
    </tr>
  )
}

export default function WaterAnalysis({ 
  title, 
  subtitle, 
  rows = [], 
  language = 'english'
}) {
  const t = getTranslation(language)
  const sectionTitle = title || t.waterSection1Title
  const sectionSubtitle = subtitle || t.waterSection1Subtitle
  const hasBODL = rows.some((row) =>
    String(row.actualText).toUpperCase().includes("BODL")
  );
  
  return (
    <div className="mt-4">
      <SectionBar title={sectionTitle} subtitle={sectionSubtitle} />
      <div className="border border-[#cbd9d2] rounded-md overflow-hidden">
        <table className="w-full">
          <thead>
            <HeaderRow t={t} />
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <Row key={r.parameter ?? i} index={i + 1} row={r} />
            ))}
          </tbody>
        </table>
      </div>
      {hasBODL && (
        <div className="mt-2 px-1 text-[12px] text-gray-600 italic leading-5">
          <strong>{t.note}:</strong>{" "}
          <strong className="text-red-600">
            {t.bodlLabel}
          </strong>{" "}
          {t.bodlDescription}
        </div>
      )}
    </div>
  )
}
